import { createGradient } from '@/components/charts/chart-bar-emotions.component';
import { DateTime } from 'luxon';
import { PATIENTS_MOCK } from './constants';

interface PatientView {
  id: number;
  created_at: string;
  patient_id: number;
  resource_id: number;
}

interface Resource {
  id: number;
  url: string;
  type: 'image' | 'audio' | 'playlist' | 'podcast';
  title: string | null;
  metadata: any;
  patient_id: number | null;
  youtube_id: string | null;
  thumbnail_url: string | null;
  duration_playlist: number | null;
  resources_patient_views: PatientView[];
}

interface ResourceWithViewCount extends Resource {
  viewCount: number;
}

/**
 * Filtra y ordena recursos según las vistas dentro de un rango de fechas
 * @param resources - Array de recursos con sus vistas
 * @param startDate - Fecha inicial del rango (DateTime de Luxon)
 * @param endDate - Fecha final del rango (DateTime de Luxon)
 * @returns Array de recursos ordenados por número de vistas en el rango de fechas
 * @throws Error si las fechas no son válidas o si startDate es posterior a endDate
 */
export const filterAndSortResourcesByDateRange = (
  resources: Resource[],
  startDate: DateTime,
  endDate: DateTime
): Resource[] => {
  if (!startDate.isValid || !endDate.isValid) {
    throw new Error('Invalid DateTime objects provided');
  }

  if (startDate > endDate) {
    throw new Error('Start date must be before or equal to end date');
  }

  const filteredResources = resources.map((resource): ResourceWithViewCount => {
    const filteredViews = resource.resources_patient_views.filter((view) => {
      const viewDate = DateTime.fromISO(view.created_at);
      return viewDate >= startDate && viewDate <= endDate;
    });

    return {
      ...resource,
      resources_patient_views: filteredViews,
      viewCount: filteredViews.length,
    };
  });

  const sortedResources = filteredResources.sort(
    (a, b) => b.viewCount - a.viewCount
  );

  return sortedResources.map(({ viewCount, ...resource }) => resource);
};

export const safeJSONParse = <T>(
  value: FormDataEntryValue | null,
  fallback: T
): T => {
  if (typeof value !== 'string') {
    return fallback;
  }
  try {
    return JSON.parse(value) as T;
  } catch (error) {
    return fallback;
  }
};

export function formatDurationComplete(seconds: number) {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`;
  }
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

export const getPatientById = (patientId: string) => {
  return PATIENTS_MOCK.find((p) => p.id === patientId);
};

export const generateQuotePrice = (
  patientsNumber: number,
  isAnual: boolean,
  plansArray: {
    key: string;
    monthly: { price: number; price_id: string };
    yearly: { price: number; price_id: string };
  }[]
): { finalPrice: number; priceId: string } => {
  const plans = plansArray.map((plan) => {
    return {
      patients: parseInt(plan.key.split('_')[1]),
      price: isAnual ? plan.yearly.price : plan.monthly.price,
      price_id: isAnual ? plan.yearly.price_id : plan.monthly.price_id,
    };
  });

  let selectedPlan = plans[0];
  for (let i = 0; i < plans.length; i++) {
    if (patientsNumber <= plans[i].patients) {
      selectedPlan = plans[i];
      break;
    }
  }
  if (patientsNumber > 30) {
    selectedPlan = plans[plans.length - 1];
  }
  return {
    finalPrice: selectedPlan.price,
    priceId: selectedPlan.price_id,
  };
};

export const hexToRGBA = (hex: string, alpha = 1) => {
  // Remover el símbolo # si está presente
  hex = hex.replace('#', '');

  // Parsear el valor hexadecimal
  let r, g, b;
  if (hex.length === 3) {
    // Si es un formato corto (#RGB)
    r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
    g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
    b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
  } else if (hex.length === 6) {
    // Si es un formato largo (#RRGGBB)
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else {
    throw new Error('Formato HEX inválido');
  }

  // Asegurarse de que alpha esté entre 0 y 1
  alpha = Math.min(1, Math.max(0, alpha));

  // Retornar el string RGBA
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const validateUUID = (code: string) => {
  const regex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  return regex.test(code);
};

export const hasDuplicates = (arr: string[]): boolean => {
  return new Set(arr).size !== arr.length;
};
export const findNextDate = (data: any[], field: string = 'created_at') => {
  if (!data.length) return null;
  const today = DateTime.now().startOf('day');

  const nextDate = data.reduce((closest, current) => {
    if (!current[field]) return closest;

    const currentDate = DateTime.fromISO(current[field]);

    if (currentDate < today) return closest;

    if (!closest || !closest[field]) return current;

    const closestDate = DateTime.fromISO(closest[field]);

    return currentDate < closestDate ? current : closest;
  }, null);

  return nextDate;
};
export const findRecentlyCreated = (
  data: any[],
  field: string = 'created_at'
) => {
  if (!data.length) return null;
  const lastCreated = data.reduce((latest, current) => {
    if (!current[field]) return null;
    if (!latest[field]) return null;

    const currentDate = DateTime.fromISO(current[field]);
    const latestDate = DateTime.fromISO(latest[field]);

    return currentDate > latestDate ? current : latest;
  });

  return lastCreated;
};

export const sortByDate = (data: any[] = [], dateField: string) => {
  const array = [...data];
  return array.sort((a, b) => {
    const fields = dateField.split('.');

    const getNestedValue = (obj: any, fields: string[]) => {
      return fields.reduce(
        (value, field) =>
          value && value[field] !== undefined ? value[field] : null,
        obj
      );
    };

    const dateA = getNestedValue(a, fields);
    const dateB = getNestedValue(b, fields);

    if (dateA === null && dateB === null) return 0;

    if (dateA === null) return 1;
    if (dateB === null) return -1;

    const dateTimeA = DateTime.fromISO(dateA);
    const dateTimeB = DateTime.fromISO(dateB);

    return dateTimeA.toMillis() - dateTimeB.toMillis();
  });
};

export function debounce<T extends (...args: any[]) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export enum EmotionsValue {
  veryBad = 0,
  bad = 1,
  somewhatBad = 2,
  normal = 3,
  somewhatGood = 4,
  good = 5,
  veryGood = 6,
}

export const generateDataChartWeekActivity = (
  health_data: { date_from: string; value: number }[]
): number[] => {
  const weekData = Array(7).fill(0);

  health_data.forEach((data) => {
    const weekday = DateTime.fromISO(data.date_from).weekday % 7;
    weekData[weekday] += data.value;
  });

  return weekData;
};

export const generateDataEmotionsWeek = (data: any[]): number[][] => {
  const chart = Array.from({ length: 7 }, () => Array(7).fill(0));

  data.forEach((e: any) => {
    const weekDay = DateTime.fromISO(e.created_at).weekday % 7;
    const emotionIndex = EmotionsValue[e.mood] as unknown as number;
    if (emotionIndex !== undefined) {
      chart[weekDay][emotionIndex] += 1;
    }
  });

  return chart;
};

export const generateDataChartWeekAutoregistry = (
  autoregistry: { response_date: string; value: number }[]
): number[] => {
  const weekData = Array(7).fill(0);

  autoregistry.forEach((data) => {
    const weekday = DateTime.fromISO(data.response_date).weekday % 7;
    weekData[weekday] += 1;
  });

  return weekData;
};

export const generateDataChartWeekDiary = (
  diaries: { date: string; value: number }[]
): number[] => {
  const weekData = Array(7).fill(0);

  diaries.forEach((data) => {
    const weekday = DateTime.fromISO(data.date).weekday % 7;
    weekData[weekday] += 1;
  });

  return weekData;
};

export const calculateTotalSum = (healthData: any[]): number => {
  return healthData.reduce((total, data) => total + data.value, 0);
};

export const generateDataChartWeekResources = (
  views: PatientView[]
): number[] => {
  if (!views?.length) {
    return [];
  }

  const weekData = Array(7).fill(0);

  views.forEach((view) => {
    const weekday = DateTime.fromISO(view.created_at).weekday;

    const arrayIndex = weekday % 7;
    weekData[arrayIndex]++;
  });

  return weekData;
};

export const generateDataChartWeek = (data: any[]): number[] => {
  const chart = Array(7).fill(0);
  const count = Array(7).fill(0);

  data.forEach((e: any) => {
    const weekDay = DateTime.fromISO(e.created_at).weekday % 7;
    chart[weekDay] += EmotionsValue[e.mood];
    count[weekDay] += 1;
  });

  const response = chart.map((total, index) =>
    count[index] > 0 ? Math.floor(total / count[index]) : 0
  );
  return response;
};

export const generateDataColorChartWeek = (context: any, data: number[]) => {
  const chart = data.map((e) => {
    if (e === 0)
      return createGradient(
        context,
        'rgba(191, 176, 232, 1)',
        'rgba(191, 176, 232, 1)'
      );
    if (e === 1)
      return createGradient(
        context,
        'rgba(159, 137, 220, 1)',
        'rgba(159, 137, 220, 0.5)'
      );
    if (e === 2)
      return createGradient(
        context,
        'rgba(240, 187, 224, 1)',
        'rgba(212, 139, 189, 1)'
      );
    if (e === 3)
      return createGradient(
        context,
        'rgba(189, 235, 209, 1)',
        'rgba(189, 235, 209, 1)'
      );
    if (e === 4) return createGradient(context, '#B0E7C9', '#8ECDAA');
    if (e === 5)
      return createGradient(
        context,
        'rgba(225, 194, 100, 1)',
        'rgba(225, 194, 100, 0.7)'
      );
    if (e === 6)
      return createGradient(
        context,
        'rgba(250, 227, 158, 1)',
        'rgba(250, 227, 158, 0.5)'
      );
  });

  return chart;
};

export const generateDataBackgroundChartWeek = (data: number[]) => {
  if (!data)
    return Array.from({ length: 7 }).map((e) => 'rgba(191, 176, 232, 0.2)');
  const chart = data.map((e) => {
    if (e === 0) return 'rgba(191, 176, 232, 0.2)';
    if (e === 1) return 'rgba(159, 137, 220, 0.2)';
    if (e === 2) return 'rgba(240, 187, 224, 0.2)';
    if (e === 3) return 'rgba(189, 235, 209, 0.2)';
    if (e === 4) return 'rgba(176, 231, 201, 0.2)';
    if (e === 5) return 'rgba(225, 194, 100, 0.2)';
    if (e === 6) return 'rgba(250, 227, 158, 0.2)';
  });
  return chart;
};

export const isDateInCurrentWeek = (date: DateTime): boolean => {
  const now = DateTime.now();

  const startOfWeek = now.startOf('week').minus({ days: 1 });
  const endOfWeek = now.endOf('week').minus({ days: 1 });

  return date >= startOfWeek && date <= endOfWeek;
};

export const getLatestRecordsQuestionnaries = (data: any[]) => {
  const sortedData = [...data].sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;

    return (
      DateTime.fromISO(a.sent_at).toMillis() -
      DateTime.fromISO(b.sent_at).toMillis()
    );
  });

  const lastRecord = (code: string) => {
    const filteredData = sortedData
      .filter((item) => item)
      .filter((submission) => submission.questionnaire?.code === code);

    if (filteredData.length === 0) return undefined;

    return filteredData.reduce((latest, current) =>
      DateTime.fromISO(latest.sent_at) > DateTime.fromISO(current.sent_at)
        ? latest
        : current
    );
  };

  const latestBAI = lastRecord('BAI');
  const latestBDI = lastRecord('BDI-II');

  return [latestBAI, latestBDI]
    .filter(Boolean)
    .sort(
      (a, b) =>
        DateTime.fromISO(b.sent_at).toMillis() -
        DateTime.fromISO(a.sent_at).toMillis()
    );
};

export const getLatestRecordsAlliance = (data: any[]) => {
  const sortedData = (data ? [...data] : []).sort((a, b) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;

    return (
      DateTime.fromISO(a.sent_at).toMillis() -
      DateTime.fromISO(b.sent_at).toMillis()
    );
  });

  const lastRecord = (code: string) => {
    const filteredData = sortedData
      .filter((item) => item)
      .filter((submission) => submission.questionnaire?.code === code);

    if (filteredData.length === 0) return undefined;

    return filteredData.reduce((latest, current) =>
      DateTime.fromISO(latest.sent_at) > DateTime.fromISO(current.sent_at)
        ? latest
        : current
    );
  };

  const latestWAIS = lastRecord('WAIS');

  return [latestWAIS]
    .filter(Boolean)
    .sort(
      (a, b) =>
        DateTime.fromISO(b.sent_at).toMillis() -
        DateTime.fromISO(a.sent_at).toMillis()
    );
};

export function isDateBetween(
  date: DateTime,
  startDate: DateTime,
  endDate: DateTime
): boolean {
  return date >= startDate && date <= endDate;
}

type DepressionLevel =
  | 'Estos altibajos son considerados normales.'
  | 'Leve perturbación del estado de ánimo.'
  | 'Estados de depresión intermitentes.'
  | 'Depresión moderada.'
  | 'Depresión grave.'
  | 'Depresión extrema.'
  | 'Puntuación fuera de rango.';

export const getDepressionLevel = (score: number): DepressionLevel => {
  if (score >= 0 && score <= 10) {
    return 'Estos altibajos son considerados normales.';
  } else if (score >= 11 && score <= 16) {
    return 'Leve perturbación del estado de ánimo.';
  } else if (score >= 17 && score <= 20) {
    return 'Estados de depresión intermitentes.';
  } else if (score >= 21 && score <= 30) {
    return 'Depresión moderada.';
  } else if (score >= 31 && score <= 40) {
    return 'Depresión grave.';
  } else if (score >= 41) {
    return 'Depresión extrema.';
  } else {
    return 'Puntuación fuera de rango.';
  }
};

type AnsietyLevel =
  | 'Ansiedad muy baja.'
  | 'Ansiedad muy moderada.'
  | 'Ansiedad muy severa.';

export const getAnsietyLevel = (score: number): AnsietyLevel => {
  if (score >= 0 && score <= 21) {
    return 'Ansiedad muy baja.';
  } else if (score >= 35) {
    return 'Ansiedad muy moderada.';
  } else {
    return 'Ansiedad muy severa.';
  }
};

type AllianceLevel = 'Alta puntuación.' | 'Baja puntuación.';

export const getAllianceLevel = (score: number): AllianceLevel => {
  if (score >= 0 && score <= 50) {
    return 'Baja puntuación.';
  } else {
    return 'Alta puntuación.';
  }
};

type Symptom = {
  id: string;
  value: {
    symptom: string;
    level: number[];
  };
};

type Note = {
  id: number;
  title: string;
  created_at: string;
  symptomatology: string | null;
};

type Dataset = {
  label: string;
  data: number[];
  backgroundColor: (context: any) => CanvasGradient | null;
};

type Result = {
  dailySymptomCounts: number[];
  mostCommonSymptoms: string[];
  datasets: Dataset[];
  monthLabels: string[];
};

export const processNotes = (
  notes: Note[],
  startDate: DateTime,
  endDate: DateTime,
  colors: [string, string] = ['#FF6347', '#FF4500']
): Result => {
  const monthsDiff = endDate.diff(startDate, 'months').months;
  const monthCount = Math.ceil(monthsDiff) + 1;

  const monthlySymptomCounts = Array(monthCount).fill(0);
  const symptomCounter: { [key: string]: number } = {};
  const symptomIntensity: { [key: string]: number[][] } = {};

  const monthLabels = [];
  for (let i = 0; i < monthCount; i++) {
    const month = startDate.plus({ months: i });
    monthLabels.push(month.toFormat('MMM yyyy'));
  }

  notes.forEach((note) => {
    const noteDate = DateTime.fromISO(note.created_at);
    if (noteDate >= startDate && noteDate <= endDate) {
      const monthIndex = Math.floor(noteDate.diff(startDate, 'months').months);

      if (note.symptomatology) {
        let symptoms: Symptom[] = [];
        try {
          symptoms = JSON.parse(note.symptomatology);
        } catch (e) {
          console.error('Error parsing symptomatology:', e);
        }

        if (!Array.isArray(symptoms)) symptoms = [symptoms];

        symptoms.forEach((symptom) => {
          const symptomName = symptom.value.symptom;
          symptomCounter[symptomName] = (symptomCounter[symptomName] || 0) + 1;

          if (!symptomIntensity[symptomName]) {
            symptomIntensity[symptomName] = Array(monthCount)
              .fill(null)
              .map(() => []);
          }

          symptom.value.level.forEach((level) => {
            symptomIntensity[symptomName][monthIndex].push(level);
          });

          monthlySymptomCounts[monthIndex] += symptom.value.level.length;
        });
      }
    }
  });

  const datasets: Dataset[] = Object.entries(symptomIntensity).map(
    ([symptom, monthlyLevels]) => ({
      label: symptom,
      data: monthlyLevels.map((levels) => {
        if (levels.length === 0) return 0;
        const total = levels.reduce((sum, level) => sum + level, 0);
        return total / levels.length;
      }),
      backgroundColor: (context: any) => {
        const chart = context.chart;
        const { ctx, chartArea } = chart;

        if (!chartArea) {
          return null;
        }

        const gradient = ctx.createLinearGradient(
          0,
          chartArea.bottom,
          0,
          chartArea.top
        );
        gradient.addColorStop(0, colors[0]);
        gradient.addColorStop(1, colors[1]);

        return gradient;
      },
    })
  );

  const mostCommonSymptoms = Object.entries(symptomCounter)
    .sort((a, b) => b[1] - a[1])
    .map(([symptom]) => symptom);

  return {
    dailySymptomCounts: monthlySymptomCounts,
    mostCommonSymptoms,
    datasets,
    monthLabels,
  };
};

export const getLevel = (level: number) => {
  if (level >= 0 && level <= 32) return 'Ligero';
  if (level >= 33 && level <= 65) return 'Medio';
  if (level >= 66 && level <= 99) return 'Fuerte';
  if (level >= 100) return 'Severo';
};
